import { createClient } from "@supabase/supabase-js";
import {
  getLocalTenantConfig,
  getTenantConfigEndpoint,
  getTenantContext,
  isValidTenantId,
  normalizeTenantId,
  shouldUseRuntimeTenantLookup,
} from "./tenantConfig";

const clientCache = new Map();
let initializationPromise = null;
let initializationError = null;

const buildClient = (config) => {
  return createClient(config.url, config.anonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
    },
  });
};

const cacheClient = (tenantId, config) => {
  if (clientCache.has(tenantId)) {
    return clientCache.get(tenantId);
  }
  const client = buildClient(config);
  clientCache.set(tenantId, client);
  return client;
};

const fetchRuntimeTenantConfig = async ({ hostname, tenantId }) => {
  const endpoint = getTenantConfigEndpoint();
  const query = new URLSearchParams({ hostname, tenantId }).toString();
  const response = await fetch(`${endpoint}?${query}`, {
    method: "GET",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    return null;
  }

  const payload = await response.json();
  const normalizedTenantId = normalizeTenantId(payload.tenantId);
  if (!isValidTenantId(normalizedTenantId)) {
    throw new Error("[supabaseClient] Runtime tenant config returned invalid tenant id.");
  }

  const url = typeof payload.url === "string" ? payload.url.trim() : "";
  const anonKey = typeof payload.anonKey === "string" ? payload.anonKey.trim() : "";
  if (!url || !anonKey) {
    throw new Error("[supabaseClient] Runtime tenant config missing Supabase credentials.");
  }

  return {
    tenantId: normalizedTenantId,
    config: { url, anonKey },
  };
};

const resolveTenantConfig = async () => {
  const context = getTenantContext();
  if (!isValidTenantId(context.tenantId)) {
    throw new Error(
      `[supabaseClient] No tenant subdomain detected for host "${context.hostname}". Use "<school_id>.localhost" or "<school_id>.${process.env.REACT_APP_ROOT_DOMAIN || "v2.edthos.app"}".`
    );
  }

  const localConfig = getLocalTenantConfig(context.tenantId);
  if (localConfig) {
    return {
      tenantId: context.tenantId,
      config: localConfig,
      source: "env",
    };
  }

  if (shouldUseRuntimeTenantLookup()) {
    const runtimeConfig = await fetchRuntimeTenantConfig(context);
    if (runtimeConfig) {
      return {
        tenantId: runtimeConfig.tenantId,
        config: runtimeConfig.config,
        source: "runtime",
      };
    }
  }

  throw new Error(
    `[supabaseClient] No tenant config found for "${context.tenantId}" on host "${context.hostname}".`
  );
};

export const initializeSupabaseClient = async () => {
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = resolveTenantConfig()
    .then(({ tenantId, config }) => {
      initializationError = null;
      return cacheClient(tenantId, config);
    })
    .catch((error) => {
      initializationError = error;
      throw error;
    });

  return initializationPromise;
};

export const getSupabaseClient = () => {
  const { tenantId } = getTenantContext();
  const cachedClient = clientCache.get(tenantId);
  if (cachedClient) {
    return cachedClient;
  }

  const localConfig = getLocalTenantConfig(tenantId);
  if (localConfig) {
    return cacheClient(tenantId, localConfig);
  }

  if (initializationError) {
    throw initializationError;
  }

  throw new Error(
    `[supabaseClient] Client for tenant "${tenantId}" not initialized. Call initializeSupabaseClient() before using supabase.`
  );
};

export const getSupabaseInitializationError = () => initializationError;

export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const client = getSupabaseClient();
      const value = client[prop];
      if (typeof value === "function") {
        return value.bind(client);
      }
      return value;
    },
  }
);
